<script setup lang="ts">
import { computed, ref } from 'vue';
import { useDisplay, useTheme } from 'vuetify';
import { useRouter } from 'vue-router';
import {
    BUserMenu,
    BList,
    BBtn,
    BIcon,
    BBtnIcon,
    BImg,
    BPageActions,
    BTooltip,
    BMenu,
    BListItem,
    BListItemTitle,
    BChip,
} from '@/design-system';
import BNavigationItems from '@/design-system/components/navigation/BNavigationItems.vue';
import { useAuthStore } from '@/stores/authStore';
import type { NavigationEntry, NavigationItemType } from '@/types/NavigationItemType';
import LogoSvg from '@/assets/img/logo-byulo.svg?component';
import { SharedRoutes, CompanyAdminRoutes } from '@/types/enums/RouteNameEnums';
import router from '@plugins/router';
import { useLayoutState } from '@/composables/useLayoutState';
import {
    useFavoriteNavigation, useTranslation, usePlatform, useNavigationScroll,
} from '@/composables';
import { usePageActionsStore } from '@/stores/pageActionsStore';
import { routeTitle } from '@/utils/navUtils';
import rfidBuilderIcon from '@/assets/img/icon-rfid-builder.webp';
import { storeToRefs } from 'pinia';
import { useAcl } from 'vue-simple-acl';
import { FeatureKeyEnum } from '@/types/enums/FeatureKeyEnum';

withDefaults(defineProps<PropsType>(), { menuItems: () => [], bottomMenuItems: () => [] });
const { xs } = useDisplay();
const theme = useTheme();
const sidebarWidth = theme.current.value.variables['sidebar-width'];
const sidebarRailWidth = theme.current.value.variables['sidebar-rail-width'];
const routerInstance = useRouter();
const authStore = useAuthStore();
const { t } = useTranslation();
const { formatShortcut } = usePlatform();
const { hasPageActions, pageActions } = storeToRefs(usePageActionsStore());
const isSuperAdmin = computed((): boolean => !!authStore.user?.is_super_admin);
const isDemo = computed((): boolean => !!authStore.user?.tenant?.is_demo);
const { can } = useAcl();
const canRfidBuilder = computed((): boolean => can(FeatureKeyEnum.RfidBuilder));

type PropsType = {
    menuItems?: NavigationEntry[];
    bottomMenuItems?: NavigationItemType[];
};

const drawerState = useLayoutState({ key: 'drawer', defaultValue: true });
const railState = useLayoutState({ key: 'rail', defaultValue: false });

const drawer = computed(() => drawerState.state.value);
const rail = computed(() => railState.state.value);

const loadOpenedGroups = (): string[] => {
    const stored = localStorage.getItem('layout-openedGroups');

    return stored ? JSON.parse(stored) : [];
};

const saveOpenedGroups = (groups: string[]) => {
    localStorage.setItem('layout-openedGroups', JSON.stringify(groups));
};

const opened = ref<string[]>(loadOpenedGroups());

const handleUpdate = (value: string[]) => {
    opened.value = value;
    saveOpenedGroups(value);
};

const handleDrawerToggle = () => {
    railState.state.value = false;
    drawerState.state.value = !drawerState.state.value;
    railState.save();
    drawerState.save();
};

const handleRailToggle = () => {
    railState.state.value = !railState.state.value;
    railState.save();
};

const handleExpandFromRail = () => {
    railState.state.value = false;
    railState.save();
};

const handleAiChatClick = (): void => {
    routerInstance.push({ name: CompanyAdminRoutes.AiChat });
};

const handleRfidBuilderClick = (): void => {
    routerInstance.push({ name: CompanyAdminRoutes.RfidBuilder });
};

const impersonatingUserText = computed(() => {
    if (!authStore.user) {
        return '';
    }

    const userName = `${authStore.user.first_name} ${authStore.user.last_name}`;
    const tenantName = authStore.user.tenant?.name;

    return tenantName ? `${userName} (${tenantName})` : `${userName}`;
});

const favoriteNav = useFavoriteNavigation();

const currentNavigationKey = computed(() => {
    return router.currentRoute.value.meta.navigationKey as string | undefined;
});

const isFavorite = computed(() => {
    if (!currentNavigationKey.value) {
        return false;
    }

    return favoriteNav.isFavorite(currentNavigationKey.value);
});

const canAddToFavorite = computed(() => {
    return favoriteNav.canAddToFavorite() && !!currentNavigationKey.value;
});

const handleToggleFavorite = async () => {
    if (currentNavigationKey.value) {
        await favoriteNav.toggleFavorite(currentNavigationKey.value);
    }
};

const getFavoriteTooltip = computed(() => {
    return isFavorite.value ? t('navigation.removeFromFavorites') : t('navigation.addToFavorites');
});

const pageInfoTitle = computed((): string => {
    const key = router.currentRoute.value.meta.pageTitle as string | undefined;
    if (!key) {
        return '';
    }
    const result = t(`pageDescription.${key}.title`);

    return result !== `pageDescription.${key}.title` ? result : '';
});

const pageInfoDescription = computed((): string => {
    const key = router.currentRoute.value.meta.pageTitle as string | undefined;
    if (!key) {
        return '';
    }
    if (pageInfoTitle.value) {
        return t(`pageDescription.${key}.description`);
    }
    const flat = t(`pageDescription.${key}`);

    return flat !== `pageDescription.${key}` ? flat : t(`pageTitle.${key}`);
});

const pageInfoNote = computed((): string => {
    const key = router.currentRoute.value.meta.pageTitle as string | undefined;
    if (!key) {
        return '';
    }
    const result = t(`pageDescription.${key}.note`);

    return result !== `pageDescription.${key}.note` ? result : '';
});

const isAiChatActive = computed(
    () =>
        router.currentRoute.value.name === CompanyAdminRoutes.AiChat ||
        router.currentRoute.value.name === CompanyAdminRoutes.AiChatDetail,
);

const init = () => {
    useNavigationScroll({ isRail: rail });
};

init();
</script>

<template>
    <VAppBar
        :elevation="0"
        color="background-layout"
        class="d-flex b-app-bar"
        :class="{ 'is-impersonating': authStore.isImpersonating }"
    >
        <div class="app-bar-container w-100 mx-5">
            <RouterLink
                :to="{ name: SharedRoutes.Home }"
                class="d-flex mt-2"
            >
                <LogoSvg style="min-width: 80px; height: auto" />
            </RouterLink>

            <VSpacer />

            <div
                v-if="authStore.isImpersonating"
                class="d-flex align-center ga-2 pa-2 rounded bg-warning-lighten-4 border border-warning mr-3"
            >
                <BIcon color="warning-darken-2">mdi-account-switch</BIcon>
                <span class="text-caption">
                    {{ t('common.impersonatingUser') }}:&nbsp;
                    <span class="font-weight-bold">
                        {{ impersonatingUserText }}
                    </span>
                </span>
                <BBtn
                    @click="authStore.returnToSelf"
                    icon
                    color="warning"
                    variant="text"
                    density="compact"
                    size="small"
                >
                    <BIcon>mdi-logout</BIcon>
                </BBtn>
            </div>

            <slot name="header-actions" />

            <BMenu
                v-if="!isSuperAdmin"
                location="bottom end"
                :close-on-content-click="false"
                list-classes="header-dropdown-menu"
            >
                <template #activator="{ props: menuProps }">
                    <BBtn
                        v-bind="menuProps"
                        icon
                        size="default"
                        variant="text"
                        color="default"
                        density="compact"
                        class="mr-1"
                    >
                        <BIcon
                            size="small"
                            color="grey-700"
                        >mdi-bell-outline</BIcon
                        >
                    </BBtn>
                </template>
                <template #list-items>
                    <BListItem>
                        <BListItemTitle class="text-center text-grey-700 py-2">
                            {{ t('layout.noNotifications') }}
                        </BListItemTitle>
                    </BListItem>
                </template>
            </BMenu>

            <BMenu
                v-if="!isSuperAdmin"
                location="bottom end"
                :close-on-content-click="false"
                list-classes="header-dropdown-menu py-1"
            >
                <template #activator="{ props: menuProps }">
                    <BBtn
                        v-bind="menuProps"
                        icon
                        size="default"
                        variant="text"
                        color="default"
                        density="compact"
                        class="mr-2"
                    >
                        <BIcon
                            size="small"
                            color="grey-700"
                        >mdi-help</BIcon
                        >
                    </BBtn>
                </template>
                <template #list-items>
                    <BListItem density="compact">
                        <BListItemTitle class="d-flex align-center">
                            <BIcon
                                icon="mdi-phone"
                                class="mr-3 opacity-60"
                                size="small"
                                color="grey-800"
                            />
                            <span>
                                <a
                                    href="tel:+420605750966"
                                    class="help-value text-grey-700 text-decoration-none"
                                >
                                    +420&nbsp;605&nbsp;750&nbsp;966
                                </a>
                            </span>
                        </BListItemTitle>
                    </BListItem>
                    <BListItem density="compact">
                        <BListItemTitle class="d-flex align-center">
                            <BIcon
                                icon="mdi-email-outline"
                                class="mr-3 opacity-60"
                                size="small"
                                color="grey-800"
                            />
                            <span>
                                <a
                                    href="mailto:support@buylo.ai"
                                    class="help-value text-grey-700 text-decoration-none"
                                >
                                    support@buylo.ai
                                </a>
                            </span>
                        </BListItemTitle>
                    </BListItem>
                </template>
            </BMenu>

            <BChip
                v-if="isDemo"
                variant="outlined"
                color="ai-red"
                size="small"
                class="mr-1 px-4 chip--is-demo"
            >
                <span class="chip--is-demo-text">{{ t('layout.demo') }}</span>
            </BChip>

            <BUserMenu
                avatar-class="ml-2"
                list-class="header-dropdown-menu"
            />
        </div>
    </VAppBar>

    <VNavigationDrawer
        :model-value="drawer"
        @update:model-value="
            (value: boolean) => {
                drawerState.state.value = value;
                drawerState.save();
            }
        "
        :rail="rail"
        :temporary="xs"
        :permanent="!xs"
        :width="sidebarWidth"
        :rail-width="sidebarRailWidth"
        color="background-layout"
        class="primary-menu pt-5"
        data-driver-tour="primary-menu"
    >
        <template
            #prepend
            v-if="!isSuperAdmin"
        >
            <div
                class="d-flex w-100 justify-center align-center mb-2"
                :class="{ 'px-5': !rail }"
            >
                <BBtn
                    flat
                    border
                    :rounded="rail ? 'circle' : true"
                    :icon="rail"
                    :size="rail ? 'small' : 'default'"
                    class="btn-ai-chat text-grey-700 text-none px-4"
                    :class="{
                        'btn-ai-chat-active': isAiChatActive,
                        'w-100': !rail,
                    }"
                    data-driver-tour="btn-ai-chat"
                    @click="handleAiChatClick"
                >
                    <div class="d-flex align-center ga-3 btn-ai-chat-content justify-space-between w-100 px-2">
                        <BIcon size="18">mdi-message-text-outline</BIcon>
                        <template v-if="!rail">
                            <span class="label">{{ t('common.aiChat') }}</span>
                            <span class="keyboard-shortcut border rounded text-disabled text-caption"
                            ><span>{{ formatShortcut('K') }}</span></span
                            >
                        </template>
                    </div>
                </BBtn>
            </div>
        </template>

        <BList
            v-model:opened="opened"
            nav
            @update:opened="event => handleUpdate(event)"
            :class="{ 'px-5': !rail }"
        >
            <template
                v-for="(item, index) in menuItems"
                :key="('key' in item ? item.key : undefined) || `heading-${index}`"
            >
                <!-- Section with wrapper div -->
                <div
                    v-if="'items' in item"
                    class="navigation-section"
                    :data-driver-tour="item.sectionKey"
                >
                    <span
                        class="menu-section-heading"
                        :class="{ 'border-t-md w-100': rail && index !== 0, 'd-none': rail && index === 0 }"
                    >
                        <span
                            class="text-grey-700 font-weight-bold"
                            :class="{ 'opacity-0': rail }"
                        >{{ item.heading }}</span
                        >
                    </span>
                    <BNavigationItems
                        :items="item.items"
                        :rail="rail"
                        :current-navigation-key="currentNavigationKey"
                        @expand-rail="handleExpandFromRail"
                    />
                </div>

                <template v-else-if="!('heading' in item)">
                    <BNavigationItems
                        :items="[item]"
                        :rail="rail"
                        :current-navigation-key="currentNavigationKey"
                        @expand-rail="handleExpandFromRail"
                    />
                </template>
            </template>
        </BList>

        <BBtn
            v-if="xs && drawer"
            @click="handleDrawerToggle"
            icon
            variant="outlined"
            color="white"
            density="comfortable"
            class="d-flex position-absolute bottom-0 right-0 ma-4 z-index-10"
        >
            <BIcon color="white">mdi-menu-open</BIcon>
        </BBtn>

        <BBtnIcon
            v-if="!xs"
            @click="handleRailToggle"
            :icon="rail ? 'mdi-chevron-double-right' : 'mdi-chevron-double-left'"
            size="default"
            :elevation="2"
            variant="flat"
            density="compact"
            class="collapse-button"
            :class="{ 'collapse-button-left': !rail, 'collapse-button-right': rail }"
            icon-size="small"
            icon-color="grey-500"
        />

        <template
            v-if="!isSuperAdmin"
            #append
        >
            <BList
                v-if="bottomMenuItems.length > 0"
                v-model:opened="opened"
                nav
                @update:opened="event => handleUpdate(event)"
                :class="{ 'px-5': !rail }"
            >
                <BNavigationItems
                    :items="bottomMenuItems"
                    :rail="rail"
                    :current-navigation-key="currentNavigationKey"
                    @expand-rail="handleExpandFromRail"
                />
            </BList>

            <div
                v-if="canRfidBuilder"
                class="d-flex w-100 justify-center align-center mb-4"
                :class="{ 'px-6': !rail }"
            >
                <BBtn
                    flat
                    :rounded="rail ? 'circle' : true"
                    color="primary"
                    class="text-none"
                    :class="{ 'px-6 w-100': !rail }"
                    :icon="rail"
                    :size="rail ? 'small' : 'default'"
                    data-driver-tour="rfid-builder-btn"
                    @click="handleRfidBuilderClick"
                >
                    <div class="d-flex align-center ga-3">
                        <BImg
                            :src="rfidBuilderIcon"
                            :alt="t('common.rfidBuilder')"
                            :width="rail ? 20 : 16"
                            :height="rail ? 18 : 14"
                        />
                        <span v-if="!rail">{{ t('common.rfidBuilder') }}</span>
                    </div>
                </BBtn>
            </div>
        </template>
    </VNavigationDrawer>

    <BBtn
        v-if="xs && !drawer"
        @click="handleDrawerToggle"
        icon
        color="grey-600"
        variant="flat"
        class="d-flex position-fixed bottom-0 left-0 ml-4 my-4"
        :style="{ zIndex: 10 }"
    >
        <BIcon color="white">mdi-menu-close</BIcon>
    </BBtn>

    <VMain>
        <slot name="pre-page-title" />

        <div
            v-if="
                router.currentRoute.value.meta.pageTitle &&
                    !router.currentRoute.value.meta.accessDenied &&
                    !router.currentRoute.value.meta.hidePageTitle
            "
            class="page-header position-relative d-flex align-center mt-8 pb-2"
        >
            <BTooltip
                v-if="canAddToFavorite"
                location="bottom"
                :max-width="300"
            >
                <template #activator="{ props: tooltipProps }">
                    <BIcon
                        v-bind="tooltipProps"
                        :icon="isFavorite ? 'mdi-star' : 'mdi-star-outline'"
                        color="grey-700"
                        size="20"
                        class="mr-2 page-info-icon"
                        @click="handleToggleFavorite"
                    />
                </template>
                {{ getFavoriteTooltip }}
            </BTooltip>

            <h1 class="font-weight-regular page-title text-grey-600">
                {{ routeTitle(router.currentRoute.value) }}
            </h1>

            <BTooltip
                location="bottom"
                :max-width="460"
            >
                <template #activator="{ props: tooltipProps }">
                    <BIcon
                        v-bind="tooltipProps"
                        icon="mdi-information-outline"
                        color="grey-700"
                        size="19"
                        class="ml-4 page-info-icon"
                    />
                </template>
                <div v-if="pageInfoTitle">
                    <div class="font-weight-bold mb-1">{{ pageInfoTitle }}</div>
                    <div>{{ pageInfoDescription }}</div>
                    <div
                        v-if="pageInfoNote"
                        class="font-weight-bold mt-2"
                    >
                        {{ pageInfoNote }}
                    </div>
                </div>
                <template v-else>{{ pageInfoDescription }}</template>
            </BTooltip>

            <div
                id="page-actions-teleport"
                class="page-actions-teleport"
            />

            <template v-if="$slots['page-actions'] || hasPageActions">
                <slot name="page-actions">
                    <BPageActions
                        v-if="hasPageActions"
                        :actions="pageActions"
                    />
                </slot>
            </template>
        </div>

        <slot name="suf-page-title" />

        <VContainer
            class="container-content"
            fluid
        >
            <slot />
        </VContainer>

        <BBtn
            v-if="!isSuperAdmin"
            rounded
            color="ai-red"
            class="assistant-button text-transform-none"
            :class="{ 'assistant-button-statistics': router.currentRoute.value.name === CompanyAdminRoutes.Statistics }"
            data-driver-tour="assistant-button"
        >
            <div class="d-flex align-center ga-2">
                <BIcon size="small">mdi-assistant</BIcon>
                <span>{{ t('common.assistant') }}</span>
            </div>
        </BBtn>
    </VMain>
</template>

<style scoped lang="scss">
.b-app-bar {
    border-block-end-width: thin;
    border-block-end-style: solid;
    border-block-end-color: rgba(var(--v-border-color), var(--v-border-opacity));
}
.app-bar-container {
    display: flex;
    align-items: center;
    min-height: 60px;
}

.b-app-bar.is-impersonating {
    border-bottom: 2px solid #fc814a;
}

.absolute-center {
    left: 50%;
    transform: translateX(-50%);
}

.primary-menu :deep(.v-list-item__spacer) {
    width: 16px;
}
.primary-menu :deep(.v-list-group__items .v-list-item) {
    --indent-padding: 30px;
}

.primary-menu :deep(.v-list-item) {
    transition: background-color 0.2s ease-in-out;
}

.primary-menu :deep(.v-list-item-title) {
    font-weight: 400;
    color: rgba(var(--v-theme-grey-700));
}

.primary-menu :deep(.v-list-item--active),
.primary-menu :deep(.v-list-item:hover) {
    background-color: rgb(var(--v-theme-hover-bg));
}

.primary-menu :deep(.v-list-item--density-compact.v-list-item--one-line) {
    min-height: 30px;
}

.navigation-section {
    padding-block: 1rem;
}

.menu-section-heading {
    font-size: 0.78rem;
    padding-bottom: 10px;
    color: white;
    display: inline-block;
    padding-inline: 8px;
}

.navigation-section + .navigation-section {
    padding-top: 0;
}

:deep(.menu-section-heading + .v-list-item) {
    margin-top: 0;
}

.v-list > .navigation-section:first-child {
    padding-top: 0;
}

.keyboard-shortcut {
    line-height: 1.5;
    padding-inline: 0.1rem;
}
.keyboard-shortcut > span {
    font-size: 0.55rem;
}
.v-btn.btn-ai-chat {
    transition: all 0.2s ease-in-out;
    background: white !important;
}
.v-btn.v-btn--icon.btn-ai-chat {
    height: 36px;
    width: 38px;
}
.v-btn.btn-ai-chat:hover {
    border-color: rgba(var(--v-theme-grey-300));
    background: rgba(var(--v-theme-hover-bg), 0.7) !important;
}
.v-btn.btn-ai-chat-active {
    border-color: rgb(0, 59, 100);
    background: rgba(var(--v-theme-hover-bg)) !important;
    &:hover {
        border-color: rgb(0, 59, 100);
    }
}
.page-title {
    font-size: clamp(1rem, 2.5vw, 1.4rem);
}
.page-header {
    border-bottom: 1px solid rgba(var(--v-theme-grey-100));
    padding-inline: var(--page-padding-inline);
}
:deep(.v-btn.btn-ai-chat:hover > .v-btn__overlay) {
    opacity: 0;
}
:deep(.favourite-icon) {
    opacity: 0.7;
    transition: opacity 0.2s ease-in-out;

    &:hover {
        opacity: 1;
    }
}
.page-info-icon {
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.2s ease-in-out;
    &:hover {
        opacity: 1;
    }
}
.page-actions-teleport {
    display: flex;
    align-items: center;
    gap: 8px;
}
.collapse-button {
    position: absolute;
    bottom: 70px;
    left: calc(1px * (var(--v-sidebar-width) - 15));
    transition: all 0.2s;
    z-index: 2;
}
.collapse-button.collapse-button-right {
    left: calc(1px * (var(--v-sidebar-rail-width) - 16));
}
.assistant-button {
    display: none; // dočasně vypnuto / skryto
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: background-color 0.2s ease-in-out;
}
.assistant-button-statistics {
    bottom: 80px;
}
:deep(.v-container:has(> .ai-chat-page)) {
    padding: 0;
}
:deep(
    .v-list-group__header:not(.v-treeview-item--activatable-group-activator).v-list-item--active:not(:focus-visible)
        .v-list-item__overlay
) {
    opacity: calc(var(--v-activated-opacity) * var(--v-theme-overlay-multiplier));
}
.btn-ai-chat-content .label {
    font-size: 12px;
}
.container-content {
    padding-inline: var(--container-content-padding-inline);
}
.help-value {
    font-size: 0.9rem;
}
.chip--is-demo {
    border-width: 2px;
}
:deep(.chip--is-demo-text) {
    font-weight: 700;
    text-transform: uppercase;
    line-height: 1.5;
    font-size: 0.7rem;
}
</style>
